import { useAISuggestionsContext } from '@/contexts/ai-suggestions-context';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { cn, pluralize, snakeCaseToTitleCase } from '@/lib/utils';
import { AIDealSuggestion, AITaskSuggestion, Deal } from '@/models/registry';
import { capitalize } from 'lodash';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { DealPriorityBadge } from '../deal/priority/deal-priority-badge';
import { DealStageBadge } from '../deal/stage/deal-stage-badge';
import { DealStatusBadge } from '../deal/status/deal-status-badge';
import { Button } from '../ui/button';
import { PDKAIIcon } from '../ui/pdk-icons';

type AIBulkSuggestionsProps = {
  dealId: string;
  suggestions: AIDealSuggestion[];
  taskSuggestions?: AITaskSuggestion[];
  className?: string;
};

export function AIBulkSuggestions({
  dealId,
  suggestions,
  taskSuggestions = [],
  className = '',
}: AIBulkSuggestionsProps) {
  const {
    acceptSuggestion,
    dismissSuggestion,
    acceptTaskSuggestion,
    dismissTaskSuggestion,
  } = useAISuggestionsContext();
  const [isExpanded, setIsExpanded] = useState(
    suggestions.length + taskSuggestions.length <= 4,
  );
  const [expandedReasoningIds, setExpandedReasoningIds] = useState<Set<string>>(
    new Set(),
  );

  const hasSuggestions = suggestions && suggestions.length > 0;
  const hasTaskSuggestions = taskSuggestions && taskSuggestions.length > 0;

  if (!hasSuggestions && !hasTaskSuggestions) return null;

  // if none of the suggestions are for the dealId, return null
  if (
    hasSuggestions &&
    !suggestions.some((suggestion) => suggestion.deal_id === dealId)
  ) {
    return null;
  }
  if (
    hasTaskSuggestions &&
    !taskSuggestions.some((suggestion) => suggestion.deal_id === dealId)
  ) {
    return null;
  }

  const handleAcceptAll = () => {
    suggestions.forEach((suggestion) => acceptSuggestion(suggestion.id));
    taskSuggestions.forEach((suggestion) =>
      acceptTaskSuggestion(suggestion.id),
    );
  };

  const handleDismissAll = () => {
    suggestions.forEach((suggestion) => dismissSuggestion(suggestion.id));
    taskSuggestions.forEach((suggestion) =>
      dismissTaskSuggestion(suggestion.id),
    );
  };

  const toggleReasoning = (suggestionId: string) => {
    setExpandedReasoningIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(suggestionId)) {
        newSet.delete(suggestionId);
      } else {
        newSet.add(suggestionId);
      }
      return newSet;
    });
  };

  const getSuggestionSummary = (suggestion: AIDealSuggestion) => {
    // if suggested value is a string, snakeCaseToTitleCase it
    if (typeof suggestion.suggested_value === 'string') {
      return `${capitalize(suggestion.field)}: ${snakeCaseToTitleCase(suggestion.suggested_value)}`;
    }
    return `${capitalize(suggestion.field)}: ${suggestion.suggested_value}`;
  };

  const getSuggestionDisplay = (suggestion: AIDealSuggestion) => {
    const currentValue = suggestion.current_value;
    const suggestedValue = suggestion.suggested_value;

    const formatValue = (value: any) => {
      if (typeof value === 'string') {
        return snakeCaseToTitleCase(value);
      }
      return value;
    };

    // get badge component
    const BadgeComponent =
      suggestion.field == 'status' ? (
        <DealStatusBadge
          value={suggestion.suggested_value as Deal['status']}
          buttonRef={undefined}
          buttonVariant="ghost"
          className="pl-0"
        />
      ) : suggestion.field == 'stage' ? (
        <DealStageBadge
          value={suggestion.suggested_value as Deal['stage']}
          buttonRef={undefined}
          className="pl-0"
        />
      ) : suggestion.field == 'priority' ? (
        <DealPriorityBadge
          value={suggestion.suggested_value as Deal['priority']}
          buttonRef={undefined}
          className="pl-0"
        />
      ) : null;

    return {
      field: capitalize(suggestion.field),
      current: formatValue(currentValue),
      suggested: formatValue(suggestedValue),
      badge: BadgeComponent,
    };
  };

  return (
    <motion.div
      className={cn(
        'border border-border/50 rounded-lg bg-card/95 backdrop-blur-sm shadow-sm',
        'hover:shadow-md transition-shadow duration-200',
        className,
      )}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 8, opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
              <PDKAIIcon className="w-4 h-4 text-primary animate-pulse" />
            </div>
            <div>
              <h3 className="text-sm text-foreground">
                {suggestions.length + taskSuggestions.length} AI{' '}
                {pluralize(
                  suggestions.length + taskSuggestions.length,
                  'Suggestion',
                  'Suggestions',
                )}
              </h3>
              <p className="text-xs text-muted-foreground">
                Review and apply{' '}
                {pluralize(
                  suggestions.length + taskSuggestions.length,
                  'suggestion',
                  'suggestions',
                )}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8 w-8 p-0 hover:bg-accent"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="sr-only">
              {isExpanded ? 'Collapse' : 'Expand'} suggestions
            </span>
          </Button>
        </div>

        {/* Bulk Actions */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="default"
            size="sm"
            onClick={handleAcceptAll}
            className="flex items-center gap-2 h-8 px-3 text-xs"
          >
            Accept All 👍
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismissAll}
            className="flex items-center gap-2 h-8 px-3 text-xs"
          >
            Dismiss All 👎
          </Button>
        </div>

        {/* Suggestions List (Expandable) */}
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="space-y-2 pt-3 border-t border-border/50"
          >
            {suggestions.map((suggestion, index) => {
              const display = getSuggestionDisplay(suggestion);
              const hasReasoning =
                suggestion.why_blocking ||
                (suggestion.evidence && suggestion.evidence.length > 0);
              const isReasoningExpanded = expandedReasoningIds.has(
                suggestion.id,
              );

              return (
                <motion.div
                  key={suggestion.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-1 bg-muted/30 rounded-md border border-border/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Field Label */}
                      <div className="mb-1">
                        <span className="text-xs  text-muted-foreground">
                          {display.field}
                        </span>
                      </div>

                      {/* Value Change Display */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground line-through">
                          {display.current || 'empty'}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="text-foreground">
                          {display.badge ?? display.suggested}
                        </span>
                      </div>

                      {/* Show Reasoning Button */}
                      {hasReasoning && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleReasoning(suggestion.id)}
                          className="mt-2 h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          {isReasoningExpanded ? (
                            <ChevronDown className="w-3 h-3 mr-1" />
                          ) : (
                            <ChevronRight className="w-3 h-3 mr-1" />
                          )}
                          Show reasoning
                        </Button>
                      )}

                      {/* Collapsible Reasoning Section */}
                      {hasReasoning && isReasoningExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="mt-2 pl-2 border-l-2 border-border/50"
                        >
                          {/* Why Blocking */}
                          {suggestion.why_blocking && (
                            <div className="mb-2">
                              <span className="text-xs text-muted-foreground">
                                Why blocking:
                              </span>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {suggestion.why_blocking}
                              </p>
                            </div>
                          )}

                          {/* Evidence */}
                          {suggestion.evidence &&
                            suggestion.evidence.length > 0 && (
                              <div>
                                <span className="text-xs text-muted-foreground">
                                  Evidence:
                                </span>
                                <ul className="mt-0.5 space-y-0.5">
                                  {suggestion.evidence.map((item, idx) => (
                                    <li
                                      key={idx}
                                      className="text-xs text-muted-foreground flex items-start gap-1"
                                    >
                                      <span className="text-muted-foreground/70 mt-0.5">
                                        •
                                      </span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                        </motion.div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => acceptSuggestion(suggestion.id)}
                        className="h-7 w-7 p-0 hover:scale-105 transition-transform"
                      >
                        👍
                        <span className="sr-only">Accept suggestion</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => dismissSuggestion(suggestion.id)}
                        className="h-7 w-7 p-0 hover:scale-105 transition-transform"
                      >
                        👎
                        <span className="sr-only">Dismiss suggestion</span>
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Task Suggestions */}
            {taskSuggestions.map((taskSuggestion, index) => {
              const hasReasoning =
                taskSuggestion.why_needed ||
                (taskSuggestion.evidence && taskSuggestion.evidence.length > 0);
              const isReasoningExpanded = expandedReasoningIds.has(
                taskSuggestion.id,
              );

              return (
                <motion.div
                  key={taskSuggestion.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (suggestions.length + index) * 0.05 }}
                  className="p-1 bg-blue-50/30 dark:bg-blue-900/10 rounded-md border border-blue-200/30 dark:border-blue-700/30 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Task Label */}
                      <div className="mb-1">
                        <span className="text-xs  text-blue-600 dark:text-blue-400">
                          New Task
                        </span>
                        {taskSuggestion.priority && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground">
                            {capitalize(taskSuggestion.priority)} priority
                          </span>
                        )}
                      </div>

                      {/* Task title */}
                      <div className="text-xs text-foreground">
                        {taskSuggestion.title}
                      </div>

                      {/* Show Reasoning Button */}
                      {hasReasoning && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleReasoning(taskSuggestion.id)}
                          className="mt-2 h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          {isReasoningExpanded ? (
                            <ChevronDown className="w-3 h-3 mr-1" />
                          ) : (
                            <ChevronRight className="w-3 h-3 mr-1" />
                          )}
                          Show reasoning
                        </Button>
                      )}

                      {/* Collapsible Reasoning Section */}
                      {hasReasoning && isReasoningExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="mt-2 pl-2 border-l-2 border-blue-200/50 dark:border-blue-700/50"
                        >
                          {/* Why Needed */}
                          {taskSuggestion.why_needed && (
                            <div className="mb-2">
                              <span className="text-xs text-muted-foreground">
                                Why needed:
                              </span>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {taskSuggestion.why_needed}
                              </p>
                            </div>
                          )}

                          {/* Evidence */}
                          {taskSuggestion.evidence &&
                            taskSuggestion.evidence.length > 0 && (
                              <div>
                                <span className="text-xs text-muted-foreground">
                                  Evidence:
                                </span>
                                <ul className="mt-0.5 space-y-0.5">
                                  {taskSuggestion.evidence.map((item, idx) => (
                                    <li
                                      key={idx}
                                      className="text-xs text-muted-foreground flex items-start gap-1"
                                    >
                                      <span className="text-muted-foreground/70 mt-0.5">
                                        •
                                      </span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                        </motion.div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => acceptTaskSuggestion(taskSuggestion.id)}
                        className="h-7 w-7 p-0 hover:scale-105 transition-transform"
                      >
                        👍
                        <span className="sr-only">Accept task suggestion</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => dismissTaskSuggestion(taskSuggestion.id)}
                        className="h-7 w-7 p-0 hover:scale-105 transition-transform"
                      >
                        👎
                        <span className="sr-only">Dismiss task suggestion</span>
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Compact Summary (when collapsed) */}
        {!isExpanded && suggestions.length > 0 && (
          <div className="pt-2 border-t border-border/50">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {suggestions
                .slice(0, 2)
                .map((s) => getSuggestionSummary(s))
                .join(', ')}
              {suggestions.length > 2 && (
                <span className="text-primary">
                  {' '}
                  +{suggestions.length - 2} more
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

type FloatingAIBulkSuggestionsProps = {
  dealId: string;
  suggestions: AIDealSuggestion[];
  taskSuggestions?: AITaskSuggestion[];
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
};

export function FloatingAIBulkSuggestions({
  dealId,
  suggestions,
  taskSuggestions = [],
  position = 'bottom-right',
}: FloatingAIBulkSuggestionsProps) {
  const { isFeatureEnabled: isAISuggestionsEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.AI_SUGGESTIONS,
  });

  if (!isAISuggestionsEnabled) return null;

  const hasSuggestions = suggestions && suggestions.length > 0;
  const hasTaskSuggestions = taskSuggestions && taskSuggestions.length > 0;

  if (!hasSuggestions && !hasTaskSuggestions) return null;

  const positionClasses = {
    'top-right': 'fixed top-6 right-6 z-50',
    'bottom-right': 'fixed bottom-6 right-6 z-50',
    'top-left': 'fixed top-6 left-6 z-50',
    'bottom-left': 'fixed bottom-6 left-6 z-50',
  };

  return (
    <div className={positionClasses[position]}>
      <AIBulkSuggestions
        dealId={dealId}
        suggestions={suggestions}
        taskSuggestions={taskSuggestions}
        className="w-80 shadow-xl border-border/60"
      />
    </div>
  );
}
